import React from 'react'
import styled from '@emotion/styled'

import Image from 'next/image'

import { ToggleSwitch } from 'modules/ui/atoms/ToggleSwitch'
import Placeloader from 'modules/ui/atoms/Loaders/Placeloader'
import TextPlaceloader from 'modules/ui/atoms/Loaders/TextPlaceloader'

import { DEFAULT_FOCUS_STYLES, DEFAULT_THEME_COLORS } from 'css/theme'

import { UiIcon } from '../Icon'

const ICON_MAP: { [key: string]: string } = {
  designation: '/embedded/icons/designation.svg',
  dedication: '/embedded/icons/dedication.svg',
  comment: '/embedded/icons/comment.svg',
  employerMatch: '/embedded/icons/employer_match.svg',
}

export interface ListItemProps extends React.ComponentPropsWithRef<'input'> {
  /** Disables interactivity with component */
  disabled?: boolean
  /** Displays the skeleton if the contents are still loading */
  loading?: boolean
  /** Image source for the icon */
  icon: string
  /** Label text */
  label: string
  /** Subtitle text */
  subtitle: string
  /** Show Toggle Switch instead of a chevron */
  showToggleSwitch?: boolean
  /** Persists "checked" state of component, defaults to false */
  checked?: boolean
  /** Function to be called when the component is clicked */
  onClick?: (...event: any[]) => void
  /** Function to be called when component's "checked" status is changed */
  onChange?: (...event: any[]) => void
  dataId?: string
  isDrawerComplete?: boolean
}

type ListItemContainerProps = {
  disabled?: boolean
  checked?: boolean
  showToggleSwitch?: boolean
  color?: string
}

const ListItemContainerExternal = styled.li<ListItemContainerProps>(
  ({ color }) => ({
    display: 'flex',
    flexDirection: 'column',
    listStyleType: 'none',
    minHeight: '76px',
    padding: '16px',
    border: '1px solid',
    borderColor: color || DEFAULT_THEME_COLORS['--theme-color--neutral-80'],
    borderRadius: '8px',
    '&:focus-within': {
      borderRadius: '8px',
      outline: color || '2px #326CDB dotted',
      outlineOffset: '2px',
    },
    '&:hover': {
      borderColor: color || DEFAULT_THEME_COLORS['--theme-color--neutral-70'],
      boxShadow: '0px 0px 0px 2px rgba(0, 0, 0, 0.12)',
      cursor: 'pointer',
    },
    '&:focus': {
      outline: color || DEFAULT_FOCUS_STYLES['--theme--focus-outline'],
      outlineOffset: color || DEFAULT_FOCUS_STYLES['--theme--focus-outline-offset'],
    },
  }),
  ({ showToggleSwitch }) => {
    const styles: any = {}

    if (showToggleSwitch) {
      styles['&:hover'] = {
        boxShadow: '0px 0px 0px 2px rgba(0, 0, 0, 0.12)',
        cursor: 'pointer',
      }
    }

    return styles
  },
)

const ListItemContainerInternal = styled.div({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  alignItems: 'center',

  marginTop: 'auto',
  marginBottom: 'auto',
  gap: '12px',
})

const ListItemText = styled.div({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',

  marginRight: '12px',

  width: '100%',
  maxWidth: 'calc(100% - 104px)',
})

const ListItemLabel = styled.span({
  fontWeight: 400,
  fontSize: '16px',
  color: DEFAULT_THEME_COLORS['--theme-color--neutral-10'],
})

const ListItemSubtitle = styled.span({
  marginTop: '4px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',

  fontWeight: 400,
  fontSize: '12px',
  color: DEFAULT_THEME_COLORS['--theme-color--neutral-40'],
})

const ListItemChevron = styled.div({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'flex-end',

  flexGrow: 1,
})

export const ListItem = (props: ListItemProps) => {
  const {
    disabled,
    loading,
    icon,
    label,
    subtitle,
    showToggleSwitch,
    checked = false,
    onClick,
    onChange,
    children,
    dataId = '',
    isDrawerComplete = false,
    color,
  } = props

  const handleKeyDown = (e: any) => {
    if (disabled) {
      return
    }

    if (e.keyCode === 13 || e.keyCode === 32) {
      e.preventDefault()

      handleClick()
    }
  }

  const handleClick = () => {
    if (disabled) {
      return
    }

    if (showToggleSwitch) {
      if (onChange) {
        onChange()
      }

      return
    }

    if (onClick) onClick()
  }

  const doNothing = () => {
    void 0
  }

  const handleChildClick = (e: any) => {
    e.stopPropagation()
  }

  const chevronColor = DEFAULT_THEME_COLORS['--theme-color--brand-primary']

  const showChildren = showToggleSwitch ? checked : true

  return (
    <ListItemContainerExternal
      tabIndex={disabled || showToggleSwitch ? -1 : 0}
      disabled={disabled}
      checked={checked}
      role="button"
      data-id={dataId}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      color={color}
    >
      <ListItemContainerInternal>
        {loading ? (
          <Placeloader
            aria-hidden="true"
            style={{ width: '36px', height: '36px', borderRadius: '18px' }}
          />
        ) : (
          <div>
            <Image src={ICON_MAP[icon]} alt={icon} height={36} width={36} />
          </div>
        )}

        <ListItemText>
          {loading ? (
            <>
              <TextPlaceloader w="50%" fs="16px" lh="20px" style={{ margin: '0px 0px 0px' }} />
              <TextPlaceloader w="90%" fs="12px" lh="20px" style={{ margin: '0px 0px 0px' }} />
            </>
          ) : (
            <>
              <ListItemLabel role="title">{label}</ListItemLabel>
              <ListItemSubtitle role="subtitle">{subtitle}</ListItemSubtitle>
            </>
          )}
        </ListItemText>

        {!loading && (
          <ListItemChevron>
            {showToggleSwitch ? (
              <ToggleSwitch checked={checked} disabled={disabled} onChange={doNothing} />
            ) : isDrawerComplete ? (
              <UiIcon name="check_circle_filled" />
            ) : (
              <UiIcon name="chevron" color={chevronColor} />
            )}
          </ListItemChevron>
        )}
      </ListItemContainerInternal>
      {showChildren && children && (
        <div
          style={{ paddingTop: '12px', position: 'relative' }}
          onClick={handleChildClick}
          onKeyDown={handleChildClick}
        >
          {children}
        </div>
      )}
    </ListItemContainerExternal>
  )
}
